import { useMemo } from 'react';
import { normalizeDateFE } from '../../../core/normalization';
import './SellerCompletedFiling.scss';
import {
  PaymentConfirmationFile,
  SellerFilingReportToConfirm,
} from '../../../core/types';
import { isSameMonth, isSameYear } from 'date-fns';
import SellerCompletedFilingItem from './SellerCompletedFilingItem';
import { ReactComponent as FolderIcon } from '../../../icons/folder-green-xl.svg';
import { useTranslation } from 'react-i18next';

interface Props {
  reports: SellerFilingReportToConfirm[];
  onFolderBtnClick: (
    files: PaymentConfirmationFile[],
    date: Date,
    c: string
  ) => () => void;
  onClickAgenda: (r: SellerFilingReportToConfirm) => () => void;
  onAmountBtnClick: (r: SellerFilingReportToConfirm) => () => void;
}

export interface CompletedFilingItemData {
  month: Date;
  items: SellerFilingReportToConfirm[];
  amounts: {
    [key: string]: number;
  };
}

export default function SellerCompletedFiling({
  reports,
  onFolderBtnClick,
  onAmountBtnClick,
  onClickAgenda,
}: Props) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    const dates = reports
      .map((r) => {
        const d = normalizeDateFE(
          r.payment_instruction?.due_date || r.deadline_date
        );
        return d;
      })
      .filter((date, i, self) => {
        return (
          self.findIndex((d) => isSameMonth(d, date) && isSameYear(d, date)) ===
          i
        );
      })
      .sort((a, b) => {
        return b.getHours() - a.getHours();
      });

    return dates.map((d) => {
      const items = reports.filter((r) => {
        const date = normalizeDateFE(
          r.payment_instruction?.due_date || r.deadline_date
        );
        return isSameMonth(d, date) && isSameYear(d, date);
      });

      const amounts = items.reduce(
        (
          acc: {
            [key: string]: number;
          },
          item
        ) => {
          if (item.payment_instruction?.amount.currency) {
            acc[item.payment_instruction.amount.currency] =
              (acc[item.payment_instruction.amount.currency] || 0) +
              item.payment_instruction.amount.amount;
          }
          return acc;
        },
        {}
      );

      return {
        month: d,
        items,
        amounts,
      };
    });
  }, [reports]);

  return (
    <div>
      {data.length ? (
        <>
          {data
            .sort((a, b) => {
              return b.month.getTime() - a.month.getTime();
            })
            .map((d, i) => (
              <SellerCompletedFilingItem
                initialOpened={i <= 2}
                onClickAgenda={onClickAgenda}
                key={d.month.getTime()}
                data={d}
                onFolderBtnClick={onFolderBtnClick}
                onAmountBtnClick={onAmountBtnClick}
              />
            ))}
        </>
      ) : (
        <>
          <div className='Text--20 fw-800 mb-3'>{t('Past periods')}</div>
          <div
            className='flex-grow-1 d-flex align-items-center justify-content-center flex-column'
            style={{
              minHeight: 500,
            }}
          >
            <FolderIcon />
            <div className='fw-800 multiline text-center mb-5'>
              {t("You don't have any filings for past periods")}
            </div>
          </div>
        </>
      )}
      <div className='multiline text-center pt-2 Text--gray'>
        {t(
          "We don't have the archive for earlier periods. To get any numbers before March 2022,  please reach out to your manager."
        )}
      </div>
    </div>
  );
}
